import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Phone, ArrowUpRight } from 'lucide-react';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { NAV_LINKS, COMPANY } from '../data/site';
import { NAV as NAV_IDS } from '../constants/testIds';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
          scrolled ? 'glass-strong border-b border-white/10' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-[104px] md:h-[128px] flex items-center justify-between gap-6">
          <Logo size={128} className="md:!inline-flex" data-testid={NAV_IDS.logo} />

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === '/'}
                data-testid={NAV_IDS[l.testKey]}
                className={({ isActive }) =>
                  `link-underline font-mono text-[11px] tracking-[0.22em] uppercase ${isActive ? 'text-white' : 'text-white/60 hover:text-white'}`
                }
                data-active={location.pathname === l.path ? 'true' : 'false'}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid={NAV_IDS.ctaCall}
              className="shine-wrap inline-flex items-center gap-2 border border-white/20 px-4 h-11 font-mono text-[11px] tracking-[0.22em] uppercase text-white hover:bg-white hover:text-black transition-colors"
            >
              <Phone size={14} strokeWidth={1.5} />
              Appeler
            </a>
            <Link
              to="/contact"
              data-testid={NAV_IDS.ctaQuote}
              className="shine-wrap inline-flex items-center gap-2 bg-white text-black px-4 h-11 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/90 transition-colors"
            >
              Demander un devis
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className="inline-flex items-center justify-center h-11 w-11 border border-white/15 text-white"
              onClick={() => setOpen(true)}
              aria-label="Ouvrir le menu"
              data-testid={NAV_IDS.menuOpen}
            >
              <Menu size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] glass-strong flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid={NAV_IDS.mobileMenu}
          >
            <div className="flex items-center justify-between px-5 md:px-10 h-[104px] md:h-[128px] border-b border-white/10">
              <Logo size={104} />
              <button
                className="inline-flex items-center justify-center h-11 w-11 border border-white/15 text-white"
                onClick={() => setOpen(false)}
                aria-label="Fermer le menu"
                data-testid={NAV_IDS.menuClose}
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 md:px-10 py-10">
              <div className="overline mb-8">Navigation</div>
              <ul className="space-y-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.li
                    key={l.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                  >
                    <NavLink
                      to={l.path}
                      className={({ isActive }) =>
                        `block font-display italic text-4xl md:text-5xl py-3 border-b border-white/10 ${isActive ? 'text-white' : 'text-white/85 hover:text-white'}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-12 space-y-4">
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  className="flex items-center justify-between border border-white/20 px-5 h-14 font-mono text-[11px] tracking-[0.22em] uppercase text-white hover:bg-white hover:text-black transition-colors"
                >
                  <span className="flex items-center gap-3"><Phone size={16} strokeWidth={1.5} /> Appeler {COMPANY.phone}</span>
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
                <Link
                  to="/contact"
                  className="flex items-center justify-between bg-white text-black px-5 h-14 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/90 transition-colors"
                >
                  Demander un devis
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </div>

              <div className="mt-10 font-mono text-[11px] tracking-[0.14em] uppercase text-white/50 space-y-1">
                <div>{COMPANY.address}</div>
                <div>{COMPANY.city}</div>
                <a href={`mailto:${COMPANY.email}`} className="link-underline block mt-3 normal-case tracking-normal">{COMPANY.email}</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
